<script setup>
import { computed } from 'vue';
import { useAuthStore } from '../stores/auth.js';
import { useI18n } from '../i18n/index.js';
import { useSettingsStore } from '../stores/settings.js';
import { useNow } from '../composables/useElapsed.js';

/* Смена в шапке: код, название, сколько осталось.

   «Смена A» само по себе не сообщает ничего, чего человек не знал бы,
   приходя на работу. Полезен остаток: мастер по нему решает, успеет ли
   партия до закрытия, оператор — браться ли за наладку под конец.

   Время считаем от серверного: границы смены заданы настенным временем
   цеха в его часовом поясе, а планшет может стоять с уехавшими часами.
   Расхождение фиксируем один раз при получении сессии и дальше просто
   идём по местным часам — дрейф за смену пренебрежимо мал. */

const auth = useAuthStore();
const settings = useSettingsStore();
// Общий таймер экрана; свой интервал здесь не нужен — минуты меняются редко
const now = useNow();

// Поправка на расхождение часов планшета с сервером
const skew = computed(() => {
  const s = auth.shift?.serverTime;
  return s ? Date.parse(s) - auth.shiftFetchedAt : 0;
});

const serverNow = computed(() => now.value + skew.value);

const to = computed(() => (auth.shift?.to ? Date.parse(auth.shift.to) : null));
const from = computed(() => (auth.shift?.from ? Date.parse(auth.shift.from) : null));

const leftMin = computed(() => {
  if (!to.value) return null;
  return Math.max(0, Math.round((to.value - serverNow.value) / 60000));
});

// Доля пройденной смены — тонкая полоса под кодом
const progress = computed(() => {
  if (!from.value || !to.value || to.value <= from.value) return null;
  const p = (serverNow.value - from.value) / (to.value - from.value);
  return Math.min(1, Math.max(0, p));
});

const { t } = useI18n();

/* Единицы времени тоже через словарь: «ч» и «мин» — такой же текст,
   как любая подпись, и на узбекском экране они оставались русскими. */
const leftText = computed(() => {
  const m = leftMin.value;
  if (m == null) return null;
  if (m === 0) return t('shift.over');
  if (m < 60) return t('shift.minutes', { m });
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? t('shift.hoursMinutes', { h, m: r }) : t('shift.hours', { h });
});

/* Последний час подсвечиваем. Не тревога, а предупреждение: к концу смены
   надо успеть сдать выпуск и закрыть операции, иначе это ляжет на мастера
   при закрытии. */
const ending = computed(() => leftMin.value != null && leftMin.value <= 60);

/* Часы идут по поясу предприятия, а не по поясу устройства.

   Смены заданы настенным временем цеха. Планшет, привезённый из другого
   региона или просто с неверным поясом, показывал бы «14:00» рядом
   с «осталось 5 ч» при смене до 19:00 — два числа на одной строке, которые
   не сходятся. Пояс из настроек: он же используется сервером при расчёте
   границ смены, так что расхождению взяться неоткуда. */
const clock = computed(() => {
  const opts = { hour: '2-digit', minute: '2-digit' };
  const tz = settings.timezone;
  try {
    return new Date(serverNow.value)
      .toLocaleTimeString('ru-RU', tz ? { ...opts, timeZone: tz } : opts);
  } catch {
    // Настройка испорчена или пояс неизвестен движку — показываем местное
    return new Date(serverNow.value).toLocaleTimeString('ru-RU', opts);
  }
});

/* Заголовок смены. Название задаёт заказчик, и оно бывает двух видов:
   самодостаточное «Смена A (дневная)» и короткое «Дневная». В первом случае
   код уже внутри, и приписывать «Смена A» перед ним значит написать код
   дважды подряд. Проверяем, а не предполагаем. */
const title = computed(() => {
  const s = auth.shift;
  if (!s?.code) return '';
  const n = String(s.name || '').trim();
  if (!n) return t('shift.named', { code: s.code });
  const hasCode = new RegExp(`(^|[^A-Za-zА-Яа-я0-9])${s.code}([^A-Za-zА-Яа-я0-9]|$)`, 'i').test(n);
  return hasCode ? n : t('shift.namedWith', { code: s.code, name: n });
});
</script>

<template>
  <div v-if="auth.shift?.code" class="meter" :class="{ ending }">
    <div class="line">
      <span class="code">{{ title }}</span>
      <span class="clock mono">{{ clock }}</span>
    </div>

    <div class="line sub">
        <span v-if="leftText" class="left">{{ t('shift.left', { time: leftText }) }}</span>
        <span v-else class="left">{{ t('shift.noBounds') }}</span>
    </div>

    <!-- Полоса под текстом, а не отдельный виджет: она здесь подпись
         к остатку, а не самостоятельное число -->
    <div v-if="progress != null" class="track" :title="t('shift.progress', { p: Math.round(progress * 100) })">
      <span class="fill" :style="{ width: `${progress * 100}%` }" />
    </div>
  </div>
</template>

<style scoped>
.meter { display: flex; flex-direction: column; gap: 2px; min-width: 168px; }
.line { display: flex; align-items: baseline; gap: 8px; }
.code { font-size: 14px; font-weight: 600; color: var(--t1); white-space: nowrap; }
.name { font-size: var(--fs-label); color: var(--t3); white-space: nowrap;
  overflow: hidden; text-overflow: ellipsis; }
.clock { margin-left: auto; font-size: 14px; font-weight: 600; color: var(--t1); }
.sub { justify-content: space-between; }
.left { font-size: var(--fs-label); color: var(--t3); white-space: nowrap; }

.track { height: 3px; margin-top: 3px; border-radius: 2px; background: var(--surface-2); overflow: hidden; }
.fill { display: block; height: 100%; background: var(--accent); border-radius: 2px;
  transition: width var(--transition); }

/* Последний час: подсвечиваем остаток и полосу, но не код смены —
   смена не «сломалась», просто заканчивается */
.meter.ending .left { color: var(--c-warn); font-weight: 600; }
.meter.ending .fill { background: var(--c-warn); }
</style>
